import { devices_config } from '@devices/index';
import type { Device } from 'src/types/Device';
import { get_gen_2_component_channel_structure } from './interface_helpers';

export function hasOperationalCountersInStatus(
	device: Device,
	deviceStatuses: Record<string, unknown> | undefined,
): boolean {
	const key = get_gen_2_component_channel_structure(device);
	const block = deviceStatuses?.[key] as { counts?: unknown } | undefined;
	return block != null && block.counts != null && typeof block.counts === 'object';
}

export function hasActiveLoadRuntimeSupport(
	device: Device,
	deviceStatuses: Record<string, unknown> | undefined,
): boolean {
	const deviceConfig = devices_config[device?.type];
	if (!deviceConfig?.isPM) return false;
	const key = get_gen_2_component_channel_structure(device);
	const channelStatus = deviceStatuses?.[key] as { apower?: unknown } | undefined;
	return typeof channelStatus?.apower !== 'undefined';
}

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
	v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Mirrors firmware behaviour when merging a partial status delta for a single
 * component block (e.g. `switch:0`, `relays[0]`).
 */
const clearStaleTimerFields = (
	merged: Record<string, unknown>,
	incoming: Record<string, unknown>,
): void => {
	const reportsActuatorState = 'output' in incoming || 'ison' in incoming;
	if (!reportsActuatorState) return;

	const incomingHasTimer =
		'timer_started_at' in incoming || 'timer_started' in incoming || 'timer_duration' in incoming;
	if (incomingHasTimer) return;

	delete merged.timer_started_at;
	delete merged.timer_started;
	delete merged.timer_duration;
	delete merged.timer_remaining;
	if ('has_timer' in merged) merged.has_timer = false;
};

function mergeComponentBlock(
	prev: Record<string, unknown>,
	incoming: Record<string, unknown>,
): Record<string, unknown> {
	const merged = { ...prev, ...incoming };
	const pc = prev.counts;
	const ic = incoming.counts;
	if (isPlainObject(pc)) {
		if (ic === undefined || ic === null) merged.counts = pc;
		else if (isPlainObject(ic)) merged.counts = { ...pc, ...ic };
	}
	clearStaleTimerFields(merged, incoming);
	return merged;
}

export function mergePartialDeviceStatus(
	prev: Record<string, unknown> | null | undefined,
	incoming: unknown,
): Record<string, unknown> {
	const p = isPlainObject(prev) ? prev : {};
	const i = isPlainObject(incoming) ? incoming : {};
	const out: Record<string, unknown> = { ...p };
	for (const key of Object.keys(i)) {
		const pv = p[key];
		const iv = i[key];
		out[key] = isPlainObject(pv) && isPlainObject(iv) ? mergeComponentBlock(pv, iv) : iv;
	}
	return out;
}

export function formatOperationalRuntimeSeconds(totalSeconds: number): string {
	const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));

	const units = [
		{ label: 'y', value: 365 * 24 * 3600 },
		{ label: 'mo', value: 30 * 24 * 3600 },
		{ label: 'd', value: 24 * 3600 },
		{ label: 'h', value: 3600 },
		{ label: 'm', value: 60 },
	];

	let remaining = s;
	const parts: string[] = [];

	for (const unit of units) {
		if (remaining >= unit.value) {
			const qty = Math.floor(remaining / unit.value);
			remaining %= unit.value;
			parts.push(`${qty}${unit.label}`);
		}

		if (parts.length === 2) break;
	}

	if (parts.length === 0) return '0m';

	return parts.join(' ');
}

export function formatOperationalRuntimeFull(totalSeconds: number): string {
	const s = Math.max(0, Math.floor(Number(totalSeconds) || 0));

	const units = [
		{ label: 'y', value: 365 * 24 * 3600 },
		{ label: 'mo', value: 30 * 24 * 3600 },
		{ label: 'd', value: 24 * 3600 },
		{ label: 'h', value: 3600 },
		{ label: 'm', value: 60 },
	];

	let remaining = s;
	const parts: string[] = [];

	for (const unit of units) {
		if (remaining >= unit.value) {
			const qty = Math.floor(remaining / unit.value);
			remaining %= unit.value;
			parts.push(`${qty}${unit.label}`);
		}
	}

	return parts.length ? parts.join(' ') : '0m';
}

export function formatOperationalResetTimestamp(ts: number | undefined | null): string {
	const sec = ts > 1e12 ? Math.floor(ts / 1000) : Math.floor(Number(ts));
	if (!Number.isFinite(sec) || sec <= 0) return '—';
	try {
		return new Date(sec * 1000).toLocaleString(undefined, {
			dateStyle: 'medium',
			timeStyle: 'short',
		});
	} catch {
		return '—';
	}
}
